import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

const COPY = {
  de: {
    label: "Video-Überblick",
    title: "Ein klarer Überblick.",
    subtitle:
      "drei kurze Videos für das QR-Menü",
    skip: "Intro überspringen",
    start: "Videos ansehen",
    steps: [
      {
        number: 1,
        title: "Service-Überblick",
        text: "Ein kurzer Einstieg in die wichtigsten Möglichkeiten.",
      },
      {
        number: 2,
        title: "QR-Menü erstellen",
        text: "So erstellen Sie schnell ein digitales Menü mit QR-Code.",
      },
      {
        number: 3,
        title: "Fotos & Bearbeitung",
        text: "Bilder hinzufügen, Preise ändern und Inhalte verwalten.",
      },
    ],
  },

  en: {
    label: "Video overview",
    title: "One clear overview.",
    subtitle:
      "three short videos for the QR menu",
    skip: "Skip intro",
    start: "Watch videos",
    steps: [
      {
        number: 1,
        title: "Service overview",
        text: "A short introduction to the main features.",
      },
      {
        number: 2,
        title: "Create QR menu",
        text: "Learn how to quickly create a digital menu with a QR code.",
      },
      {
        number: 3,
        title: "Photos & editing",
        text: "Add images, update prices, and manage your content.",
      },
    ],
  },
};

export default function VideoIntro({
  lang = "de",
  steps: customSteps,
  onFinish,
  autoFinish = false,
}) {
  const shouldReduceMotion = useReducedMotion();

  const copy = COPY[lang] || COPY.de;

  const steps = useMemo(() => {
    return Array.isArray(customSteps) && customSteps.length
      ? customSteps
      : copy.steps;
  }, [customSteps, copy.steps]);

  const finishRef = useRef(onFinish);

  const [activeStep, setActiveStep] = useState(-1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [introDone, setIntroDone] = useState(false);

  useEffect(() => {
    finishRef.current = onFinish;
  }, [onFinish]);

  useEffect(() => {
    const timers = [];

    setActiveStep(-1);
    setCompletedSteps([]);
    setIntroDone(false);

    if (shouldReduceMotion) {
      setCompletedSteps(steps.map((_, index) => index));
      setActiveStep(-1);
      setIntroDone(true);

      if (autoFinish) {
        const timer = window.setTimeout(() => {
          finishRef.current?.();
        }, 500);

        timers.push(timer);
      }

      return () => timers.forEach(window.clearTimeout);
    }

    steps.forEach((_, index) => {
      const startTime = 350 + index * 1700;
      const completeTime = startTime + 900;

      timers.push(
        window.setTimeout(() => {
          setActiveStep(index);
        }, startTime)
      );

      timers.push(
        window.setTimeout(() => {
          setCompletedSteps((prev) => {
            if (prev.includes(index)) return prev;
            return [...prev, index];
          });
        }, completeTime)
      );
    });

    const doneTime = 350 + steps.length * 1700 + 500;

    timers.push(
      window.setTimeout(() => {
        setActiveStep(-1);
        setIntroDone(true);

        if (autoFinish) {
          window.setTimeout(() => {
            finishRef.current?.();
          }, 900);
        }
      }, doneTime)
    );

    return () => timers.forEach(window.clearTimeout);
  }, [steps, shouldReduceMotion, autoFinish]);

  const finishIntro = () => {
    setCompletedSteps(steps.map((_, index) => index));
    setActiveStep(-1);
    setIntroDone(true);
    finishRef.current?.();
  };

  return (
    <section className="video-intro" aria-label={copy.label}>
      <motion.div
        className="video-intro__bg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
      />

      <div className="video-intro__container">
        <motion.div
          className="video-intro__content"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        >
          <h1 className="video-intro__title">{copy.title}</h1>

          <p className="video-intro__subtitle">{copy.subtitle}</p>

          <div className="video-intro__actions">
            <AnimatePresence>
              {introDone && (
                <motion.button
                  type="button"
                  className="video-intro__start"
                  onClick={finishIntro}
                  initial={{ opacity: 0, y: 12, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 12, scale: 0.96 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                >
                  {copy.start}
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        <motion.div
          className="video-intro__flow"
          initial={{ opacity: 0, x: 28 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.65, delay: 0.15, ease: "easeOut" }}
        >
          {steps.map((step, index) => {
            const isActive = activeStep === index;
            const isCompleted = completedSteps.includes(index);
            const showText = isActive || isCompleted;
            const showConnector = isCompleted && index < steps.length - 1;

            return (
              <div className="video-intro__flow-item" key={step.title}>
                <div
                  className={[
                    "video-intro__step",
                    isActive ? "is-active" : "",
                    isCompleted ? "is-completed" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <motion.div
                    className="video-intro__circle"
                    animate={{
                      scale: isActive ? 1.08 : 1,
                    }}
                    transition={{ duration: 0.25 }}
                  >
                    <AnimatePresence mode="wait">
                      {isCompleted ? (
                        <motion.svg
                          key="check"
                          className="video-intro__check"
                          viewBox="0 0 24 24"
                          initial={{ opacity: 0, scale: 0.75 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.75 }}
                          transition={{ duration: 0.22 }}
                        >
                          <motion.path
                            d="M5 12.5L10 17L19 7"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 0.38, ease: "easeOut" }}
                          />
                        </motion.svg>
                      ) : (
                        <motion.span
                          key="number"
                          className="video-intro__number"
                          initial={{ opacity: 0, scale: 0.7 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.7 }}
                          transition={{ duration: 0.22 }}
                        >
                          {step.number || index + 1}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </motion.div>

                  <AnimatePresence>
                    {showText && (
                      <motion.div
                        className="video-intro__text"
                        initial={{ opacity: 0, x: 18 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 18 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                      >
                        <h3>{step.title}</h3>
                        <p>{step.text}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {index < steps.length - 1 && (
                  <motion.div
                    className="video-intro__connector"
                    initial={false}
                    animate={{
                      opacity: showConnector ? 1 : 0,
                      height: showConnector ? 54 : 18,
                    }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                  >
                    <span />
                  </motion.div>
                )}
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}